export const NAV_ITEM_KINDS = {
  classList: 'classList',
  link: 'link',
  sectionLabel: 'sectionLabel',
}

// Curriculum-service hrefs are program-relative (e.g. /middle-school/lessons)
// while Learn serves programs under /product.
const toLearnRoute = href => `/product${href}`

const isSelectedRoute = (pathname, route) => pathname.includes(route)

const transformLink = ({ link, pathname }) => {
  const route = toLearnRoute(link.href)
  return {
    kind: NAV_ITEM_KINDS.link,
    title: link.label,
    route,
    isSelected: isSelectedRoute(pathname, route),
  }
}

const transformClassList = ({ classList, classLinks }) => ({
  kind: NAV_ITEM_KINDS.classList,
  title: classList.label,
  tier: classList.tier,
  items: classLinks
    ? classLinks
        .filter(classLink => classLink.tier === classList.tier)
        .map(({ title, route, isSelected }) => ({
          kind: NAV_ITEM_KINDS.link,
          title,
          route,
          isSelected,
        }))
    : null,
})

const isRenderableLink = item =>
  item?.kind === NAV_ITEM_KINDS.link && !!item.label && !!item.href

const isRenderableSectionLabel = item =>
  item?.kind === NAV_ITEM_KINDS.sectionLabel && !!item.label

const isRenderableClassList = item =>
  item?.kind === NAV_ITEM_KINDS.classList && !!item.label && item.tier != null

/**
 * Dropdown items for one tab. Only direct children are kept. Each classList
 * placeholder is filled with the user's classes of its tier (`items` is null
 * while classes are still loading). A section label is kept only when a link
 * sits directly under it.
 */
const transformChildren = ({ children, classLinks, pathname }) => {
  const items = (children || []).reduce((acc, child) => {
    if (isRenderableLink(child)) {
      acc.push(transformLink({ link: child, pathname }))
    } else if (isRenderableSectionLabel(child)) {
      acc.push({ kind: NAV_ITEM_KINDS.sectionLabel, title: child.label })
    } else if (isRenderableClassList(child)) {
      acc.push(transformClassList({ classList: child, classLinks }))
    }
    return acc
  }, [])

  return items.filter(
    (item, index) =>
      item.kind !== NAV_ITEM_KINDS.sectionLabel ||
      items[index + 1]?.kind === NAV_ITEM_KINDS.link,
  )
}

/**
 * Maps a GET /v1/navigation response to the `programs` shape ProgramNav
 * renders: one entry per tab, each with its dropdown `children`.
 * @param {Object} params
 * @param {Object} params.navigation - curriculum-service navigation payload
 * @param {string} [params.pathname=''] - current pathname
 * @param {Array} [params.classLinks] - the user's class links ({ tier, title,
 *   route, isSelected }), or null/undefined while they are loading
 */
export function transformCurriculumNavigation({
  navigation,
  pathname = '',
  classLinks = null,
}) {
  return (navigation?.items || []).filter(isRenderableLink).map(item => ({
    ...transformLink({ link: item, pathname }),
    children: transformChildren({
      children: item.children,
      classLinks,
      pathname,
    }),
  }))
}
